import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { LOGO_URL } from "@/components/Navbar";

function GoogleButton() {
  const handle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={handle} data-testid="google-login-btn"
      className="w-full h-11 rounded-lg border-slate-300 gap-2 font-medium">
      <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z"/></svg>
      Fortsæt med Google
    </Button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Velkommen tilbage!");
      navigate(u.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login mislykkedes");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 grain-bg">
        <Link to="/" className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="J-Computer logo" className="h-10 w-10 rounded-lg object-cover ring-1 ring-slate-700" />
          <span className="text-xl font-black tracking-tighter font-display">J-Computer<span className="text-blue-500">.</span></span>
        </Link>
        <div>
          <h2 className="text-4xl font-display font-black tracking-tighter leading-tight">
            Følger <span className="text-blue-500">DIT</span> behov.
          </h2>
          <p className="mt-4 text-slate-400 max-w-sm">
            Log ind i din kundeportal og følg dine bookinger, produkter og beskeder ét sted.
          </p>
        </div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} J-Computer</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <h1 className="text-2xl font-display font-black tracking-tight text-slate-900">Log ind</h1>
          <p className="mt-1 text-sm text-slate-500">Adgang til din kundeportal.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" type="email" required value={email} data-testid="login-email"
                onChange={(e) => setEmail(e.target.value)} placeholder="din@email.dk"
                className="h-11 bg-slate-50 focus-visible:ring-blue-600" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Adgangskode</Label>
              <Input id="password" type="password" required value={password} data-testid="login-password"
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="h-11 bg-slate-50 focus-visible:ring-blue-600" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700">
              {loading ? "Logger ind…" : "Log ind"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
            <div className="h-px flex-1 bg-slate-200" /> ELLER <div className="h-px flex-1 bg-slate-200" />
          </div>
          <GoogleButton />

          <p className="mt-8 text-sm text-slate-600 text-center">
            Har du ikke en konto?{" "}
            <Link to="/register" className="text-blue-600 font-medium hover:underline" data-testid="to-register">Opret her</Link>
          </p>
          <p className="mt-2 text-xs text-slate-400 text-center">
            Ny konto skal godkendes af J-Computer — medmindre du laver en booking.
          </p>
        </div>
      </div>
    </div>
  );
}
